import { ref, type Ref } from "vue";

import type { Controller, WorkspaceId } from "@ctrl/controller-rpc";

import { createAttachment } from "@/utils/attachments";

/** Subset of controller Attachment passed to the AI send path. */
export type AiAttachment = {
  id: string;
  name: string;
  url: string;
  mime: string;
};

/** Alias kept for call-sites that used the old distinct type. */
export type ChatAttachment = AiAttachment;

/** A file currently being uploaded (not yet finalised). */
export type UploadingFile = {
  key: number;
  name: string;
  mime: string;
  progress: number;
};

export type UseAttachments = {
  attachments: Ref<ChatAttachment[]>;
  uploading: Ref<UploadingFile[]>;
  isUploading: Ref<boolean>;
  addFiles: (files: File[]) => Promise<void>;
  remove: (index: number) => void;
  clear: () => void;
  /** Seed the list with pre-existing attachment URLs (e.g. from pendingMessage). */
  seed: (items: ChatAttachment[]) => void;
};

export function useAttachments(
  controller: Controller,
  workspaceId: Ref<WorkspaceId | undefined>
): UseAttachments {
  const attachments = ref<ChatAttachment[]>([]);
  const uploading = ref<UploadingFile[]>([]);
  const isUploading = ref(false);
  let pendingUploads = 0;
  let nextKey = 0;

  async function addFiles(files: File[]): Promise<void> {
    const wsId = workspaceId.value;
    if (!wsId || files.length === 0) return;

    pendingUploads += files.length;
    isUploading.value = true;

    const uploads = files.map(async (file) => {
      const key = nextKey++;
      uploading.value = [
        ...uploading.value,
        {
          key,
          name: file.name,
          mime: file.type || "application/octet-stream",
          progress: 0,
        },
      ];

      try {
        const attachment = await createAttachment(
          wsId,
          controller,
          file,
          (progress) => {
            uploading.value = uploading.value.map((e) =>
              e.key === key ? { ...e, progress } : e
            );
          }
        );
        attachments.value = [
          ...attachments.value,
          {
            id: attachment.id,
            url: attachment.url,
            name: attachment.name,
            mime: attachment.mime,
          },
        ];
      } finally {
        uploading.value = uploading.value.filter((e) => e.key !== key);
        pendingUploads--;
        if (pendingUploads === 0) isUploading.value = false;
      }
    });

    await Promise.allSettled(uploads);
  }

  function remove(index: number): void {
    attachments.value = attachments.value.filter((_, i) => i !== index);
  }

  function clear(): void {
    attachments.value = [];
  }

  function seed(items: ChatAttachment[]): void {
    attachments.value = [...items];
  }

  return { attachments, uploading, isUploading, addFiles, remove, clear, seed };
}
